import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Mountain } from 'lucide-react';

// Extract mobile menu into separate component
const MobileMenu = ({ isOpen, navLinks, isActive, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t">
      <div className="px-4 py-2 space-y-1">
        {navLinks.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            onClick={onClose}
            className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
              isActive(link.path)
                ? 'bg-sage-700 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {link.name}
          </Link>
        ))}
      </div>
    </div>
  );
};

// Helper function to get link styles (replaces nested ternary)
const getLinkClassName = (isActivePath, needsDarkText) => {
  if (isActivePath) {
    return 'bg-sage-700 text-white';
  }
  if (needsDarkText) {
    return 'text-gray-700 hover:bg-gray-100';
  }
  return 'text-white hover:bg-white/10';
};

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  // Only the home page has a full-bleed hero with dark overlay
  const isHomePage = location.pathname === '/';
  const needsDarkText = !isHomePage || isScrolled;

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const navLinks = [
    { name: 'The Awakening', path: '/' },
    { name: 'Your Legacy', path: '/legacy' },
    { name: 'The Opportunity', path: '/opportunity' },
    { name: 'The Sanctuary', path: '/sanctuary' },
    { name: 'The Strategy', path: '/strategy' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      needsDarkText ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="bg-sage-700 p-2 rounded-lg group-hover:bg-sage-800 transition-colors">
              <Mountain className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className={`font-bold text-lg leading-tight transition-colors ${
                needsDarkText ? 'text-gray-900' : 'text-white'
              }`}>
                Legacy & Landscapes
              </div>
              <div className={`text-xs transition-colors ${
                needsDarkText ? 'text-gray-600' : 'text-white/90'
              }`}>
                High-Yield Heritage
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  getLinkClassName(isActive(link.path), needsDarkText)
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              needsDarkText ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white/10'
            }`}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <MobileMenu 
        isOpen={isMobileMenuOpen} 
        navLinks={navLinks} 
        isActive={isActive} 
        onClose={() => setIsMobileMenuOpen(false)} 
      />
    </nav>
  );
};

export default Navigation;
